import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, LayoutGrid, Rows3, ScanBarcode, Check, CheckCircle2 } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Table } from '@/components/ui/Table';
import { useToast } from '@/components/ui/useToast';
import { confirmStockEntry, pendingStockEntry, type Grn } from '@/api/grn';
import { formatDate, formatINRSymbol } from '@/lib/format';
import { apiError } from '@/lib/apiError';
import { GrnScanModal } from './GrnScanModal';

type ViewMode = 'card' | 'table';
const VIEW_STORAGE_KEY = 'stock-entry.view';

export function StockEntryPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const targetGrnId = searchParams.get('grn');
  const scrolledRef = useRef(false);

  const [scanOpen, setScanOpen] = useState(false);
  const [scanGrn, setScanGrn] = useState<Grn | null>(null);
  const [highlightId, setHighlightId] = useState<number | null>(null);
  const [view, setView] = useState<ViewMode>(() => {
    try {
      const v = localStorage.getItem(VIEW_STORAGE_KEY);
      return v === 'table' ? 'table' : 'card';
    } catch {
      return 'card';
    }
  });
  useEffect(() => {
    try { localStorage.setItem(VIEW_STORAGE_KEY, view); } catch { /* private mode */ }
  }, [view]);

  const query = useQuery({
    queryKey: ['grn-pending-stock-entry'],
    queryFn: pendingStockEntry,
    refetchInterval: 30_000,
  });

  const confirmMutation = useMutation({
    mutationFn: confirmStockEntry,
    onSuccess: (grn) => {
      qc.invalidateQueries({ queryKey: ['grn-pending-stock-entry'] });
      qc.invalidateQueries({ queryKey: ['grn'] });
      qc.invalidateQueries({ queryKey: ['po'] });
      qc.invalidateQueries({ queryKey: ['wh-stock'] });
      toast.pushStep(`Stock entry confirmed — ${grn.grnNo}`, {
        body: 'Warehouse stock updated. Check the Warehouse Stock page.',
        href: '/inventory/wh-stock',
        nextLabel: 'View Warehouse Stock',
        tag: `stock-confirmed-${grn.id}`,
      });
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Confirm failed'), 'error');
    },
  });

  function openScan(grn: Grn) {
    setScanGrn(grn);
    setScanOpen(true);
  }

  function handleScanVerified() {
    qc.invalidateQueries({ queryKey: ['grn-pending-stock-entry'] });
    qc.invalidateQueries({ queryKey: ['grn'] });
    // Keep modal open so user can see "All verified" message and confirm
  }

  // Newest card on top. Backend re-fetches every 30s (refetchInterval above)
  // so any freshly-created GRN pops to the top on the next tick.
  const data = (query.data ?? []).slice().sort((a, b) => {
    const dt = new Date(b.grnDate).getTime() - new Date(a.grnDate).getTime();
    return dt !== 0 ? dt : b.id - a.id;
  });

  // Scroll to and briefly highlight the GRN the user came from (via ?grn=<id>).
  useEffect(() => {
    if (!targetGrnId || scrolledRef.current || data.length === 0) return;
    const id = Number(targetGrnId);
    const found = data.find((g) => g.id === id);
    if (!found) return;
    scrolledRef.current = true;
    setHighlightId(id);
    setTimeout(() => {
      document.getElementById(`grn-card-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    setTimeout(() => setHighlightId(null), 2500);
    // Clear the query param so a refresh doesn't re-scroll.
    setSearchParams({}, { replace: true });
  }, [targetGrnId, data, setSearchParams]);

  return (
    // Full-height page: view toggle stays pinned at top, GRN cards scroll
    // in their own container with chromeless scrollbar (Table view already
    // handles its own scroll internally).
    <div className="flex h-[calc(100vh-72px)] min-h-0 flex-col md:h-[calc(100vh-88px)]">
      <SectionHeader
        title="Stock Entry"      />

      <div className="mb-2 inline-flex gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-1">
        <button
          type="button"
          onClick={() => setView('card')}
          className={
            'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-bold transition sm:text-xs ' +
            (view === 'card'
              ? 'bg-accent text-white shadow-sm'
              : 'text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text-primary)]')
          }
          title="Card layout — full item breakdown per GRN"
        >
          <LayoutGrid size={13} /> Cards
        </button>
        <button
          type="button"
          onClick={() => setView('table')}
          className={
            'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-bold transition sm:text-xs ' +
            (view === 'table'
              ? 'bg-accent text-white shadow-sm'
              : 'text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text-primary)]')
          }
          title="Table layout — one row per GRN"
        >
          <Rows3 size={13} /> Table
        </button>
      </div>

      {query.isFetching && data.length === 0 ? (
        <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-8 text-center text-sm text-[var(--text-muted)]">
          Loading&hellip;
        </div>
      ) : data.length === 0 ? (
        <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-10 text-center text-sm text-[var(--text-muted)]">
          No GRNs awaiting stock entry
        </div>
      ) : view === 'table' ? (
        <Table<Grn>
          storageKey="stock-entry"
          title="Pending Stock Entry"
          count={`${data.length} GRN${data.length === 1 ? '' : 's'} awaiting confirmation`}
          rows={data}
          rowKey={(r) => r.id}
          columns={[
            {
              key: 'grnNo',
              header: 'GRN #',
              cell: (r) => <span className="font-extrabold text-accent">{r.grnNo}</span>,
            },
            { key: 'date', header: 'Date', cell: (r) => formatDate(r.grnDate) },
            { key: 'subject', header: 'Subject', width: '200px', cell: (r) => <span className="block max-w-[200px] truncate" title={r.subject ?? undefined}>{r.subject || '—'}</span> },
            { key: 'po', header: 'PO Ref', cell: (r) => r.po?.poNo ?? '—' },
            {
              key: 'vendor',
              header: 'Vendor',
              cell: (r) => <span className="font-bold">{r.vendor.name}</span>,
            },
            {
              key: 'items',
              header: 'Items',
              align: 'right',
              cell: (r) => r.items.length,
            },
            {
              key: 'received',
              header: 'Received Qty',
              align: 'right',
              cell: (r) => {
                const sum = r.items.reduce((acc, it) => acc + Number(it.receivedQty), 0);
                // Only show the unit when every line shares it — otherwise the
                // number is unit-less because Pcs + Kg can't be added.
                const units = new Set(r.items.map((it) => it.unit));
                return units.size === 1
                  ? `${sum} ${r.items[0].unit}`
                  : sum;
              },
            },
            {
              key: 'value',
              header: 'Value',
              align: 'right',
              cell: (r) => formatINRSymbol(r.totalValue),
            },
            {
              key: 'scan',
              header: 'Scan',
              cell: (r) =>
                r.scanStatus === 'Verified' ? (
                  <Badge tone="success">Verified</Badge>
                ) : r.scanStatus === 'Mismatch' ? (
                  <Badge tone="danger">Mismatch</Badge>
                ) : (
                  <Badge tone="info">Pending</Badge>
                ),
            },
            {
              key: 'stock',
              header: 'Stock',
              cell: (r) => <Badge tone="warning">{r.stockStatus}</Badge>,
            },
            {
              key: 'actions',
              header: '',
              width: '210px',
              cell: (r) => (
                <div className="flex flex-wrap items-center justify-center gap-1">
                  <Button
                    variant="info"
                    size="sm"
                    icon={<ScanBarcode size={12} />}
                    onClick={() => openScan(r)}
                    title="Scan QR/barcode to verify quantities"
                  >
                    Scan
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => confirmMutation.mutate(r.id)}
                    disabled={confirmMutation.isPending || r.scanStatus !== 'Verified'}
                    title={r.scanStatus !== 'Verified' ? 'Complete QR/barcode scan first' : 'Confirm stock entry'}
                  >
                    <Check size={14} className="mr-1 inline-block align-[-2px]" /> Confirm
                  </Button>
                </div>
              ),
            },
          ]}
        />
      ) : (
        <div
          className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {data.map((g: Grn) => (
            <div
              key={g.id}
              id={`grn-card-${g.id}`}
              className={
                'rounded-card border bg-[var(--card-bg)] p-3 backdrop-blur-xl transition sm:p-5 ' +
                (highlightId === g.id
                  ? 'border-accent shadow-[0_0_0_2px_var(--accent)]'
                  : 'border-[var(--border-color)]')
              }
            >
              <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-base font-extrabold text-accent">{g.grnNo}</span>
                    <Badge tone="warning">{g.stockStatus}</Badge>
                    {g.scanStatus === 'Verified' ? (
                      <Badge tone="success">Scan Verified <CheckCircle2 size={12} className="ml-1 inline-block align-[-2px]" /></Badge>
                    ) : g.scanStatus === 'Mismatch' ? (
                      <Badge tone="danger">Scan Mismatch <AlertTriangle size={12} className="ml-1 inline-block align-[-2px]" /></Badge>
                    ) : (
                      <Badge tone="info">Scan Pending</Badge>
                    )}
                  </div>
                  <div className="mt-1 text-[11px] text-[var(--text-muted)]">
                    PO: <strong className="text-[var(--text-secondary)]">{g.po?.poNo ?? '—'}</strong>
                    {' · '}Vendor: <strong className="text-[var(--text-secondary)]">{g.vendor.name}</strong>
                    {' · '}Received: {formatDate(g.grnDate)}
                  </div>
                  {g.subject && <div className="truncate mt-0.5 text-xs font-semibold text-[var(--text-secondary)]" title={g.subject}>{g.subject}</div>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="info"
                    size="sm"
                    icon={<ScanBarcode size={14} />}
                    onClick={() => openScan(g)}
                    title="Scan QR/barcode to verify quantities"
                  >
                    Scan Items
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => confirmMutation.mutate(g.id)}
                    disabled={confirmMutation.isPending || g.scanStatus !== 'Verified'}
                    title={g.scanStatus !== 'Verified' ? 'Complete QR/barcode scan first' : 'Confirm stock entry and update warehouse stock'}
                  >
                    <span className="sm:hidden"><Check size={14} className="mr-1 inline-block align-[-2px]" /> Confirm</span>
                    <span className="hidden sm:inline"><Check size={14} className="mr-1 inline-block align-[-2px]" /> Confirm Stock Entry</span>
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto rounded-md border border-[var(--border-color)] bg-white/[0.02]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-accent/[0.05] text-[10px] font-black uppercase tracking-wide text-accent">
                      <th className="px-3 py-1.5 text-left">Item</th>
                      <th className="px-3 py-1.5 text-right">PO Qty</th>
                      <th className="px-3 py-1.5 text-right">Received</th>
                      <th className="px-3 py-1.5 text-right">Short</th>
                      <th className="px-3 py-1.5 text-right">Total</th>
                      <th className="px-3 py-1.5 text-center">Quality</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.items.map((it) => (
                      <tr key={it.id} className="border-b border-white/[0.04] last:border-b-0">
                        <td className="px-3 py-1.5 font-bold">{it.itemName}</td>
                        <td className="px-3 py-1.5 text-right">{String(it.poQty)} {it.unit}</td>
                        <td className="px-3 py-1.5 text-right">{String(it.receivedQty)}</td>
                        <td className="px-3 py-1.5 text-right">
                          <span className={Number(it.shortQty) > 0 ? 'text-warning font-bold' : 'text-[var(--text-muted)]'}>
                            {String(it.shortQty)}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-right">{formatINRSymbol(it.total)}</td>
                        <td className="px-3 py-1.5 text-center">
                          <Badge
                            tone={
                              it.quality === 'OK' ? 'success' : it.quality === 'Damaged' ? 'warning' : 'danger'
                            }
                          >
                            {it.quality}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {g.qualityRemarks && (
                <div className="mt-2 text-xs italic text-[var(--text-muted)]">QC: {g.qualityRemarks}</div>
              )}
              {g.scanStatus !== 'Verified' && (
                <div className="mt-2 flex items-center gap-2 rounded border border-amber-500/40 bg-amber-500/15 px-3 py-2 text-xs font-semibold text-amber-600 dark:text-amber-300">
                  <AlertTriangle size={14} className="flex-shrink-0" />
                  <span>
                    Scan all items using{' '}
                    <span className="inline-flex items-center gap-1 rounded bg-amber-500/20 px-1.5 py-0.5 font-bold">
                      <ScanBarcode size={11} />
                      Scan Items
                    </span>{' '}
                    before confirming stock entry.
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {scanGrn && (
        <GrnScanModal
          open={scanOpen}
          grnId={scanGrn.id}
          grnNo={scanGrn.grnNo}
          onClose={() => { setScanOpen(false); setScanGrn(null); }}
          onVerified={handleScanVerified}
        />
      )}
    </div>
  );
}